import { useContext, useEffect, useState } from 'react';
import { ThemeContext } from '../../../../../apps/frontend/src/context/ThemeStore';

import PercentileCardLight from '../../../../../apps/frontend/public/img/percentile-card-light.svg';
import PercentileCardDark from '../../../../../apps/frontend/public/img/percentile-card-dark.svg';
import PercentileCardMobileDark from '../../../../../apps/frontend/public/img/percentile-card-mobile-dark.svg';
import PercentileCardMobileLight from '../../../../../apps/frontend/public/img/percentile-card-mobile-light.svg';

import RankCardLight from '../../../../../apps/frontend/public/img/rank-card-light.svg';
import RankCardDark from '../../../../../apps/frontend/public/img/rank-card-dark.svg';
import RankCardMobileDark from '../../../../../apps/frontend/public/img/rank-card-mobile-dark.svg';
import RankCardMobileLight from '../../../../../apps/frontend/public/img/rank-card-mobile-light.svg';

import ProjectedPercentileCardLight from '../../../../../apps/frontend/public/img/projected-percentile-card-light.svg';
import ProjectedPercentileCardDark from '../../../../../apps/frontend/public/img/projected-percentile-card-dark.svg';
import ProjectedPercentileCardMobileDark from '../../../../../apps/frontend/public/img/projected-percentile-card-mobile-dark.svg';
import ProjectedPercentileCardMobileLight from '../../../../../apps/frontend/public/img/projected-percentile-card-mobile-light.svg';

import QuizrrLight from '../../../public/Images/QuizrrWhite.svg';
import QuizrrBlack from '../../../public/Images/QuizrrBlack.svg';
import QuizrrLogoLight from '../../../public/Images/QuizrrLogoLight.svg';
import QuizrrLogoDark from '../../../public/Images/QuizrrLogoDark.svg';
import { SparkleIcon } from '@phosphor-icons/react';
import { ArrowRightIcon } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';

function getTimeUntil(targetDate) {
  const now = Date.now();
  const target = new Date(targetDate).getTime();
  const diff = Math.max(0, Math.floor((target - now) / 1000));
  const days = Math.floor(diff / 86400);
  const hours = Math.floor((diff % 86400) / 3600);
  const minutes = Math.floor((diff % 3600) / 60);
  const seconds = diff % 60;
  return { days, hours, minutes, seconds, totalSeconds: diff };
}

const PercentileCard = ({
  percentileData,
  percentile,
  isProjected = false,
  isPercentileDelayed = false,
  percentileAvailableDate = null,
  type = null,
  linkedTests = null,
  packId = null,
  isCombined = false,
}) => {
  const isSubjectPercentilePresent = percentile?.subjectWisePercentile?.length > 0;
  const { isDarkTheme } = useContext(ThemeContext);
  const [isMobile, setIsMobile] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);

  const navigate = useNavigate();
  const linkedTest = linkedTests?.[0] ?? null;
  const shouldShowAttemptButton = !!(linkedTest && percentileData?.combinedScore == 'null');

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 767px)');

    setIsMobile(mediaQuery.matches);

    const handler = (e) => setIsMobile(e.matches);
    mediaQuery.addEventListener('change', handler);

    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (!isPercentileDelayed || !percentileAvailableDate) return;
    const update = () => setTimeLeft(getTimeUntil(percentileAvailableDate));
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [isPercentileDelayed, percentileAvailableDate]);

  const getBackgroundImage = () => {
    if (isMobile) {
      return isProjected
        ? isDarkTheme
          ? ProjectedPercentileCardMobileDark
          : ProjectedPercentileCardMobileLight
        : type === 'jeeAdvanced' || type === 'neet'
          ? isDarkTheme
            ? RankCardMobileDark
            : RankCardMobileLight
          : isDarkTheme
            ? PercentileCardMobileDark
            : PercentileCardMobileLight;
    }
    return isProjected
      ? isDarkTheme
        ? ProjectedPercentileCardDark
        : ProjectedPercentileCardLight
      : type === 'jeeAdvanced' || type === 'neet'
        ? isDarkTheme
          ? RankCardDark
          : RankCardLight
        : isDarkTheme
          ? PercentileCardDark
          : PercentileCardLight;
  };

  return (
    <div
      className="h-[258px] md:max-w-[496px] md:w-full rounded-xl flex-1 lg:flex-initial lg:min-w-0 flex-shrink-0 border border-[var(--q3-stroke-light)] relative"
      style={{
        backgroundImage: `url(${getBackgroundImage()}`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'top right',
        backgroundSize: 'auto 100%',
      }}
    >
      <div className={`flex flex-col justify-between h-full ${isProjected ? 'py-6' : 'py-3'} px-4`}>
        {/* Top Content */}
        <div>
          {isProjected && (
            <img src={isDarkTheme ? QuizrrLight : QuizrrBlack} alt="Quizrr Logo" className="h-[80px] w-[80px]" />
          )}
          <div
            className={`text-sm md:text-base flex flex-row items-center gap-1 mt-4 ${isProjected ? 'text-[var(--q3-teal-normal)]' : 'text-[var(--q3-indigo-normal)] font-bold'} whitespace-nowrap`}
          >
            {isProjected ? (
              <SparkleIcon size={isMobile ? 16 : 20} weight="fill" />
            ) : (
              <img
                src={isDarkTheme ? QuizrrLogoDark : QuizrrLogoLight}
                alt="Quizrr Logo"
                className={`-mt-1.5 ${(type === 'jeeAdvanced' || type === 'neet') && 'h-[70px] w-[70px]'}`}
              />
            )}
            {isProjected ? 'Potential' : type === 'jeeAdvanced' || type === 'neet' ? '' : 'Predicted'}{' '}
            {type === 'jeeAdvanced' || type === 'neet' ? '' : 'Percentile'}
          </div>
          {(type === 'jeeAdvanced' || type === 'neet') && (
            <p className="mt-2 md:text-lg text-[var(--q3-indigo-normal)]">Predicted Rank (Range)</p>
          )}
          {percentile?.data && (
            <p
              className={`${!isProjected ? 'text-[var(--q3-indigo-normal)]' : 'text-[var(--q3-teal-normal)]'} ${type === 'jeeAdvanced' || type === 'neet' ? `text-4xl md:text-5xl ${percentile?.data && (linkedTests || isCombined) ? 'pt-3 md:pt-6' : 'pt-5 md:pt-10'} font-bold` : 'text-5xl md:text-[88px] pt-0 md:pt-3 font-black'}`}
            >
              {Number(percentile?.data) ? Number(percentile?.data)?.toFixed(2) : percentile?.data}
            </p>
          )}
          {/* {!percentile?.combinedScore && percentile?.data && !isProjected && (
            <p class
            Name="text-xs text-[var(--q3-neutral-light)]">Exact Percentile: {percentile?.data}%ile</p>
          )} */}
          {!percentile?.data && <p className="text-xs max-w-[280px] mt-8">{percentileData?.consolationText}</p>}
          {isPercentileDelayed && (
            <div className="text-sm md:text-lg flex flex-col items-center justify-center gap-1 mt-10 md:mt-16">
              <p>
                Predicted Percentile is being calculated. Please check back in{' '}
                {timeLeft != null && timeLeft.totalSeconds > 0 && (
                  <span className="font-semibold text-[var(--q3-indigo-normal)]">
                    {timeLeft.days}d {String(timeLeft.hours).padStart(2, '0')}h{' '}
                    {String(timeLeft.minutes).padStart(2, '0')}m {String(timeLeft.seconds).padStart(2, '0')}s
                  </span>
                )}
              </p>
            </div>
          )}

          {percentile?.data && (linkedTests || isCombined) && (
            <p className="text-xs font-medium text-[var(--q3-neutral-light)] mt-2">{`Based upon your Paper 1 & Paper 2 attempt`}</p>
          )}

          {isSubjectPercentilePresent && (
            <div className="flex flex-row items-center justify-start gap-5 my-3 md:mt-6">
              {percentile.subjectWisePercentile.map((subject) => (
                <div key={subject?.subject?.title} className="flex flex-col items-start justify-center">
                  <p className="text-[10px] text-[var(--q3-neutral-light)]">{subject?.subject?.title}</p>
                  <p className="text-[10px] md:text-xs text-[var(--q3-neutral-light)] font-bold">
                    {Number(subject?.percentile?.data)
                      ? `${subject?.percentile?.title ? `${subject?.percentile?.title} ` : ''} ${Number(subject?.percentile?.data)?.toFixed(2)}%ile`
                      : `${subject?.percentile?.title ? `${subject?.percentile?.title} ` : ''} ${subject?.percentile?.data}%ile`}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bottom Description */}
        <div>
          <p className="text-xs text-[var(--q3-neutral-default)] mt-3 md:mt-0">
            {!isProjected
              ? extractData(percentileData?.description)
                ? `Prediction is done based on ${extractData(percentileData?.description)} data`
                : percentileData?.description
              : percentileData?.description}
          </p>
          {shouldShowAttemptButton && (
            <Button
              className="w-full flex items-center gap-1 justify-center rounded-full px-3 mt-2 bg-[var(--q3-accent-normal)] transition-colors duration-300 hover:bg-[var(--q3-accent-dark)] group"
              onClick={() => navigate(`/${packId}/${linkedTest._id}/details`)}
            >
              <span className="text-[var(--q3-neutral-default_inverted)]">Attempt {linkedTest.shortTitle}</span>
              <ArrowRightIcon size={18} />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PercentileCard;

function extractData(text) {
  const regex = /JEE Main \d{4} \(\d{1,2} [A-Za-z]+ Shift \d+\)/;
  const match = text?.match(regex);
  return match ? match[0] : null;
}
